import {
  type ItemSetCondition,
  type ItemSetCreation,
  type SearchMyTasksQuery,
  WorkflowCode,
  WorkflowStepCode,
} from '@exam-management/graphql';
import { dayjs } from '@shared/utils';

type MyTask = SearchMyTasksQuery['wflSearchMyTasks']['tasks'][number];

const WORKFLOW_CODE_TASK_TYPE_MAP = {
  [WorkflowCode.QuestionReview]: 'question-review',
  [WorkflowCode.ReviewQSet]: 'qset-review',
  [WorkflowCode.QuestionCreation]: 'question-creation',
} as const;

const TASK_COUNT_LABEL_MAP = {
  [WorkflowCode.QuestionReview]: 'Questions',
  [WorkflowCode.ReviewQSet]: 'Subtasks',
  [WorkflowCode.QuestionCreation]: 'Questions',
} as const;

type ViewableWorkflowCode =
  | WorkflowCode.QuestionReview
  | WorkflowCode.ReviewQSet
  | WorkflowCode.QuestionCreation;

function getDueStatus(dueAt: Date, completedAt: Date | null) {
  if (completedAt) return;

  const now = dayjs();
  const dueDate = dayjs(dueAt);

  if (dueDate.diff(now, 'day') < 0) {
    return 'overdue' as const;
  }

  if (dueDate.diff(now, 'day') <= 7) {
    return 'due-soon' as const;
  }
}

function getDueDateLabel(dueAt: Date, completedAt: Date | null) {
  if (completedAt) {
    return 'COMPLETED';
  }

  const now = dayjs();
  const dueDate = dayjs(dueAt);

  if (dueDate.diff(now, 'day') < 0) {
    return 'OVERDUE';
  }

  if (dueDate.diff(now, 'day') <= 7) {
    return 'DUE SOON';
  }

  return 'DUE DATE';
}

function getDueDate(task: MyTask): Date {
  if (task.completedAt) {
    return task.completedAt;
  }
  return task.dueDate as Date;
}

function getQSetReviewTaskCount(subtasks: MyTask['subtasks']): {
  completeTaskCount: number;
  totalTaskCount: number;
} {
  const totalTaskCount = subtasks.length;
  const completeTaskCount = subtasks.filter((subtask) =>
    [WorkflowStepCode.ReviewQsetReviewed, WorkflowStepCode.ReviewQsetDone].includes(
      subtask.currentWorkflowStep.code as WorkflowStepCode,
    ),
  ).length;

  return { completeTaskCount, totalTaskCount };
}

function getQuestionReviewCompleteTaskCount(task: MyTask) {
  return {
    completeTaskCount:
      task.metadata?.__typename === 'TaskMetadataWithQuestionReviewed'
        ? task.metadata.reviewedQuestionCount
        : 0,
    totalTaskCount:
      task.parentTask?.metadata.__typename === 'TaskMetadataWithReviewQuestion'
        ? task.parentTask.metadata.questionQuantity
        : 0,
  };
}

function countCompletedQuestionItemSet(itemSetCreation: ItemSetCreation[]): number {
  return itemSetCreation.reduce((count, itemSet) => {
    return count + itemSet.questionCreationCount;
  }, 0);
}

function countTotalQuestionItemSet(itemSetCondition: ItemSetCondition[]): number {
  return itemSetCondition.reduce((count, itemSet) => {
    return count + itemSet.size * itemSet.needTotal;
  }, 0);
}

function getQuestionCreationCompleteTaskCount(task: MyTask) {
  const metadata =
    task.metadata?.__typename === 'TaskMetadataWithQuestionCreation' ? task.metadata : null;
  return {
    completeTaskCount:
      (metadata?.questionCreationCount ?? 0) +
      countCompletedQuestionItemSet(metadata?.itemSetCreation ?? []),
    totalTaskCount:
      (metadata?.requiredQuestionCount ?? 0) +
      countTotalQuestionItemSet(metadata?.itemSetCondition ?? []),
  };
}

function getTaskCount(task: MyTask): {
  completeTaskCount: number;
  totalTaskCount: number;
} {
  switch (task.currentWorkflowStep.workflowCode) {
    case WorkflowCode.QuestionReview:
      return getQuestionReviewCompleteTaskCount(task);
    case WorkflowCode.ReviewQSet:
      return getQSetReviewTaskCount(task.subtasks);
    case WorkflowCode.QuestionCreation:
      return getQuestionCreationCompleteTaskCount(task);
    default:
      return { completeTaskCount: 0, totalTaskCount: 0 };
  }
}

function getTaskName(task: MyTask) {
  switch (task.currentWorkflowStep.workflowCode) {
    case WorkflowCode.QuestionReview:
      return task.parentTask?.name ?? '';
    case WorkflowCode.ReviewQSet:
    case WorkflowCode.QuestionCreation:
    default:
      return task.name ?? '';
  }
}

function getTaskCode(task: MyTask) {
  switch (task.currentWorkflowStep.workflowCode) {
    case WorkflowCode.QuestionReview:
      return task.parentTask?.code ?? '';
    case WorkflowCode.ReviewQSet:
    case WorkflowCode.QuestionCreation:
    default:
      return task.code ?? '';
  }
}

export function mapTasksToTaskCards(tasks: MyTask[]) {
  return tasks.map((task) => {
    const { completeTaskCount, totalTaskCount } = getTaskCount(task);
    return {
      id: task.id,
      type: WORKFLOW_CODE_TASK_TYPE_MAP[
        task.currentWorkflowStep.workflowCode as ViewableWorkflowCode
      ],
      title: getTaskName(task),
      code: `#${getTaskCode(task)}`,
      dateLabel: getDueDateLabel(task.dueDate as Date, task.completedAt),
      dueDate: getDueDate(task),
      dueStatus: getDueStatus(task.dueDate as Date, task.completedAt),
      completeTaskCount: completeTaskCount,
      totalTaskCount: totalTaskCount,
      taskCountLabel:
        TASK_COUNT_LABEL_MAP[task.currentWorkflowStep.workflowCode as ViewableWorkflowCode],
    };
  });
}
